import { Box, Container, Typography } from '@material-ui/core';
import React, { useCallback, useMemo, useState } from 'react';
import { DeviceDetailsModal, DeviceFilterForm, DeviceFilters, DeviceList } from '../components';
import { useFormEvents, useGuestStays, useObservable, usePageTitle } from '@lib/common';
import { PropertyDevice, getPropertyDeviceService } from 'app/state';
import { propertyQuery } from '@lib/state';
import { DownloadButton, Tile } from 'app/shared';
import { usePropertyDevices } from '../hooks';
import { lightFormat, startOfDay } from 'date-fns';
import { Alert } from '@material-ui/lab';
import { PropertyDeviceForms } from 'app/state/property-device/property-device.forms';

export function DeviceListPage() {
  usePageTitle('Property Devices');
  const propertyId = useObservable(propertyQuery.activeProperty)?.id;
  const { devices, pagination, isLoading } = usePropertyDevices(propertyId);
  const { stays, loading: guestStaysLoading } = useGuestStays(
    propertyId,
    lightFormat(startOfDay(new Date(Date.now())), 'yyyy-MM-dd')
  );
  const propertyDeviceService = getPropertyDeviceService();
  const [filters, setFilters] = useState<Partial<DeviceFilters>>({});
  const [selectedDevice, setSelectedDevice] = useState<PropertyDevice | null>(null);

  const [{ status, error }, reset] = useFormEvents(PropertyDeviceForms.IdentifyDevice);

  const occupiedRooms = useMemo(() => stays.map(s => s.roomNumber) ?? [], [stays]);

  const onFilterChange = useCallback(
    (newFilters: DeviceFilters) => {
      setFilters(newFilters);

      if (propertyId) propertyDeviceService.getDevices(propertyId, newFilters);
    },
    [setFilters, propertyId, propertyDeviceService]
  );

  const loadMore = useCallback(() => {
    if (propertyId)
      propertyDeviceService.getDevices(
        propertyId,
        filters,
        pagination.continuationToken || undefined
      );
  }, [propertyId, filters, pagination, propertyDeviceService]);

  const onIdentifyDevice = useCallback(
    (device: PropertyDevice) => {
      if (propertyId) propertyDeviceService.identifyDevice(propertyId, { ...device });
    },
    [propertyId, propertyDeviceService]
  );

  const onDeleteDevice = useCallback(
    (thingName: string) => {
      if (propertyId) propertyDeviceService.deleteDevice(propertyId, thingName);
    },
    [propertyId, propertyDeviceService]
  );

  const onRowClick = useCallback((device: PropertyDevice) => {
    setSelectedDevice(device);
  }, []);

  const onExportDevices = useCallback(() => {
    if (propertyId) propertyDeviceService.exportDevices(propertyId, filters);
  }, [propertyId, filters, propertyDeviceService]);

  return (
    <Tile>
      {status === 'success' && (
        <Alert severity="success" onClose={reset}>
          <Typography>Device identification sucessful.</Typography>
        </Alert>
      )}
      {status === 'error' && (
        <Alert severity="error" onClose={reset}>
          <Typography>Device identification failed: {error?.message}</Typography>
        </Alert>
      )}
      <Container maxWidth="lg" data-testid="deviceListPage">
        <Box display="flex" justifyContent="flex-end" flexDirection="row" p={0}>
          <Box display="flex" justifyContent="flex-end" flexDirection="row" marginRight={2}>
            <DownloadButton onDownload={onExportDevices} />
          </Box>
        </Box>
        <DeviceFilterForm onChange={onFilterChange} />
        <DeviceList
          devices={devices}
          occupiedRooms={occupiedRooms}
          loading={isLoading || guestStaysLoading}
          isDone={pagination.isDone}
          onIdentifyDevice={onIdentifyDevice}
          onDeleteDevice={onDeleteDevice}
          onRowClick={onRowClick}
          loadMore={loadMore}
        />
        <DeviceDetailsModal
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
          open={!!selectedDevice}
          onIdentifyDevice={onIdentifyDevice}
          onDeleteDevice={onDeleteDevice}
        />
      </Container>
    </Tile>
  );
}
